import React, { useState, useEffect, useRef, useCallback } from 'react';
import AppIcon from '../../../components/ui/AppIcon';
import { searchMessages } from '../../../services/chatServices';
import { useChat } from '../../../context/ChatContext';
import { useAuth } from '../../../context/AuthContext';
import HighlightedText from '../../../components/HighlightedText';

function useDebounce(value, delay = 400) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

export default function SearchOverlay({ onClose }) {
    const { selectChat, channels, directMessages, usersData, setTargetMessageId, setHighlightQuery } = useChat();
    const { user } = useAuth();
    const currentUserId = user?._id || user?.id;

    // Search state
    const [query, setQuery] = useState('');
    const [filterSenderId, setFilterSenderId] = useState('');
    const [filterChannelId, setFilterChannelId] = useState('');
    const [filterFromDate, setFilterFromDate] = useState('');
    const [filterToDate, setFilterToDate] = useState('');
    const [sortBy, setSortBy] = useState('latest');
    const [showFilters, setShowFilters] = useState(false);

    // Results state
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [source, setSource] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const LIMIT = 10;
    const inputRef = useRef(null);
    const resultRefs = useRef([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const debouncedQuery = useDebounce(query, 400);
    const requestIdRef = useRef(0); // Track latest request to avoid race conditions

    // Auto-focus input when modal opens
    useEffect(() => {
        if (true) { // isOpen always true in this overlay
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, []);

    // Reset on close
    useEffect(() => {
        return () => {
            setQuery('');
            setResults([]);
            setTotal(0);
            setPage(1);
            setHasSearched(false);
            setShowFilters(false);
            setFilterSenderId('');
            setFilterChannelId('');
            setFilterFromDate('');
            setFilterToDate('');
            setSortBy('latest');
            setSelectedIndex(-1);
        };
    }, [onClose]);

    // Reset selectedIndex whenever results change
    useEffect(() => {
        setSelectedIndex(-1);
        resultRefs.current = [];
    }, [results]);

    // Scroll highlighted item into view
    useEffect(() => {
        if (selectedIndex >= 0 && resultRefs.current[selectedIndex]) {
            resultRefs.current[selectedIndex].scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
            });
        }
    }, [selectedIndex]);

    // Keyboard navigation — uses a ref so it always sees latest results/selectedIndex
    const handleResultClickRef = useRef(null);
    const handleKeyDown = useCallback((e) => {
        switch (e.key) {
            case 'ArrowDown': {
                e.preventDefault();
                setSelectedIndex(prev =>
                    results.length === 0 ? -1 : Math.min(prev + 1, results.length - 1),
                );
                break;
            }
            case 'ArrowUp': {
                e.preventDefault();
                setSelectedIndex(prev => Math.max(prev - 1, -1));
                break;
            }
            case 'Enter': {
                if (selectedIndex >= 0 && results[selectedIndex]) {
                    e.preventDefault();
                    handleResultClickRef.current?.(results[selectedIndex]);
                }
                break;
            }
            case 'Escape': {
                onClose();
                break;
            }
            default:
                break;
        }
    }, [results, selectedIndex, onClose]);

    // ── Core search executor ──────────────────────────────────────────────────
    const doSearch = useCallback(async (pageNum = 1) => {
        const trimmedQ = debouncedQuery.trim();
        if (!trimmedQ && !filterSenderId && !filterChannelId && !filterFromDate && !filterToDate) {
            return;
        }

        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setHasSearched(true);

        // ES term filters require exact keyword strings — coerce to string and trim
        // HTML date input gives "YYYY-MM-DD"; expand to full ISO for ES range filter
        const fromISO = filterFromDate ? `${filterFromDate}T00:00:00.000Z` : undefined;
        const toISO   = filterToDate   ? `${filterToDate}T23:59:59.999Z`   : undefined;

        try {
            const params = {
                ...(trimmedQ                    && { q: trimmedQ }),
                ...(filterSenderId              && { senderId:   String(filterSenderId).trim() }),
                ...(filterChannelId             && { channelId:  String(filterChannelId).trim() }),
                ...(fromISO                     && { fromDate:   fromISO }),
                ...(toISO                       && { toDate:     toISO }),
                sortBy,
                page: pageNum,
                limit: LIMIT,
            };

            const response = await searchMessages(params);

            if (requestId === requestIdRef.current) {
                if (response?.success) {
                    if (pageNum === 1) {
                        setResults(response.results || []);
                    } else {
                        setResults((prev) => [...prev, ...(response.results || [])]);
                    }
                    setTotal(response.total || 0);
                    setSource(response.source || '');
                }
            }
        } catch (error) {
            console.error('Search error:', error);
            if (requestId === requestIdRef.current) {
                setResults([]);
                setTotal(0);
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [debouncedQuery, filterSenderId, filterChannelId, filterFromDate, filterToDate, sortBy]);

    // ── Auto-search whenever any filter or query changes ──────────────────────
    useEffect(() => {
        const trimmedQ = debouncedQuery.trim();
        const hasAnyFilter = trimmedQ || filterSenderId || filterChannelId || filterFromDate || filterToDate;

        if (hasAnyFilter) {
            setPage(1);
            doSearch(1);
        } else {
            setResults([]);
            setTotal(0);
            setHasSearched(false);
        }
    }, [debouncedQuery, filterSenderId, filterChannelId, filterFromDate, filterToDate, sortBy, doSearch]);

    // Load more (pagination)
    const loadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        doSearch(nextPage);
    };

    const hasMore = results.length < total;

    // Handle result click — navigate to the correct chat/channel
    const handleResultClick = useCallback((result) => {
        if (!result) return;

        console.log('[SearchOverlay] Clicked ES Result:', result);

        // messageId is present for ES results; MongoDB fallback uses _id instead
        const msgId = result.messageId || result._id;
        if (msgId) {
            setTargetMessageId(msgId);
            console.log('[SearchOverlay] Scrolling to Message ID:', msgId);
        }
        if (query.trim()) {
            setHighlightQuery(query.trim());
        }

        if (result.channelId) {
            // Channel message — match by channelId
            const target = channels.find(
                c => String(c._id || c.id) === String(result.channelId)
            );
            if (target) selectChat('channel', target, true);
        } else {
            // ── DM message — open chat with the OTHER person ──────────────────
            // Step 1: resolve the other user's ID.
            // NOTE: MongoDB fallback results may be missing receiverId — only
            // chatUniqueId + senderId are reliably present. Always try
            // chatUniqueId first since it's the most reliable source.
            let targetUserId;

            if (result.chatUniqueId) {
                // Most reliable: parse "smallerId_largerId" composite key
                targetUserId = result.chatUniqueId
                    .split('_')
                    .find(id => id !== String(currentUserId));
            }

            // If chatUniqueId parse didn't work (e.g. current user not in it),
            // fall back to explicit sender/receiver ID fields
            if (!targetUserId) {
                if (result.senderId && String(result.senderId) === String(currentUserId)) {
                    targetUserId = result.receiverId;
                } else if (result.receiverId && String(result.receiverId) === String(currentUserId)) {
                    targetUserId = result.senderId;
                } else {
                    targetUserId = result.receiverId || result.senderId;
                }
            }

            console.log('[SearchOverlay] Resolved Other User ID:', targetUserId);

            // Step 2: find user object — check directMessages first, then usersData,
            // then construct a minimal stub so selectChat always fires.
            let target = directMessages.find(
                dm => String(dm._id || dm.id) === String(targetUserId)
            );

            if (!target) {
                target = usersData.find(
                    u => String(u._id || u.id) === String(targetUserId)
                );
            }

            if (!target && targetUserId) {
                // Build a minimal user stub from ES result fields so the chat
                // can still open even if the user isn't in the sidebar list yet.
                // The "other user" is whoever is NOT the current user.
                // sender/receiver are name strings in MongoDB fallback results.
                const otherIsSender = String(result.senderId) !== String(currentUserId);
                const nameFromResult = otherIsSender
                    ? (typeof result.sender === 'string' ? result.sender : result.sender?.name)
                    : (typeof result.receiver === 'string' ? result.receiver : result.receiver?.name);

                target = {
                    _id: targetUserId,
                    id: targetUserId,
                    name: nameFromResult || 'Unknown',
                };
                console.log('[SearchOverlay] Built stub user from ES result:', target);
            }

            console.log('[SearchOverlay] Resolved Chat Target:', target);

            if (target) {
                selectChat('dm', target, true);
            } else {
                console.warn('[SearchOverlay] Could not resolve DM target for result:', result);
            }
        }
        onClose();
    }, [channels, directMessages, usersData, selectChat, onClose, setTargetMessageId, setHighlightQuery, query, currentUserId]);

    // Keep the ref in sync so keyboard handler always calls the latest version
    handleResultClickRef.current = handleResultClick;

    // Format timestamp
    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return (
            date.toLocaleDateString([], {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            }) +
            ' ' +
            date.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
            })
        );
    };

    // Check if result is from a channel
    const isChannelResult = (result) => !!result.channelId;

    return (
        <div className="fixed inset-0 z-[100] bg-black/20 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4 animate-fade-in">
            <div className="w-full max-w-3xl bg-app-surface border border-border-subtle rounded-3xl shadow-modal overflow-hidden flex flex-col max-h-[85vh]">
                {/* Header: Search Input */}
                <div className="flex items-center gap-4 px-6 py-5 border-b border-border-subtle bg-app-bg/30">
                    <AppIcon name="MagnifyingGlassIcon" size={20} className="text-primary" />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search messages across all chats and channels..."
                        className="flex-1 bg-transparent text-lg text-text-primary placeholder:text-text-muted outline-none"
                    />
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`p-2.5 rounded-xl transition-all shadow-subtle ${showFilters
                                ? 'bg-primary text-white shadow-primary/20 scale-105'
                                : 'text-text-muted hover:text-text-primary hover:bg-black/5 border border-transparent'
                            }`}
                        title="Filters"
                    >
                        <AppIcon name="FunnelIcon" size={20} />
                    </button>
                    <button
                        onClick={onClose}
                        className="p-2.5 rounded-xl text-text-muted hover:text-text-primary hover:bg-black/5 border border-transparent transition-all shadow-subtle"
                    >
                        <AppIcon name="XMarkIcon" size={20} />
                    </button>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="px-6 py-6 border-b border-border-subtle bg-app-bg animate-slide-down">
                        <div className="grid grid-cols-2 gap-6">
                            {/* Sender filter */}
                            <div>
                                <label className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-2 block opacity-60">
                                    <AppIcon name="UserIcon" size={12} className="inline mr-1" />
                                    Sender
                                </label>
                                <select
                                    value={filterSenderId}
                                    onChange={(e) => setFilterSenderId(e.target.value)}
                                    className="w-full text-[13px] border border-border-subtle rounded-xl px-4 py-3 bg-app-surface text-text-primary outline-none focus:border-primary/50 appearance-none cursor-pointer shadow-subtle"
                                >
                                    <option value="" className="bg-app-surface text-text-primary">All Senders</option>
                                    {usersData?.map((user) => (
                                        <option key={user._id} value={user._id} className="bg-app-surface text-text-primary">
                                            {user.name || user.email || 'Unknown'}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Channel filter */}
                            <div>
                                <label className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-2 block opacity-60">
                                    <AppIcon name="HashtagIcon" size={12} className="inline mr-1" />
                                    Channel
                                </label>
                                <select
                                    value={filterChannelId}
                                    onChange={(e) => setFilterChannelId(e.target.value)}
                                    className="w-full text-[13px] border border-border-subtle rounded-xl px-4 py-3 bg-app-surface text-text-primary outline-none focus:border-primary/50 appearance-none cursor-pointer shadow-subtle"
                                >
                                    <option value="" className="bg-app-surface text-text-primary">All Channels + DMs</option>
                                    {channels?.map((ch) => (
                                        <option key={ch._id || ch.id} value={ch._id || ch.id} className="bg-app-surface text-text-primary">
                                            # {ch.name || 'Unnamed'}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* From date */}
                            <div>
                                <label className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-2 block opacity-60">
                                    From Date
                                </label>
                                <input
                                    type="date"
                                    value={filterFromDate}
                                    onChange={(e) => setFilterFromDate(e.target.value)}
                                    className="w-full text-[13px] border border-border-subtle rounded-xl px-4 py-3 bg-app-surface text-text-primary outline-none focus:border-primary/50 appearance-none shadow-subtle"
                                />
                            </div>

                            {/* To date */}
                            <div>
                                <label className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-2 block opacity-60">
                                    To Date
                                </label>
                                <input
                                    type="date"
                                    value={filterToDate}
                                    onChange={(e) => setFilterToDate(e.target.value)}
                                    className="w-full text-[13px] border border-border-subtle rounded-xl px-4 py-3 bg-app-surface text-text-primary outline-none focus:border-primary/50 appearance-none shadow-subtle"
                                />
                            </div>
                        </div>

                        {/* Sort + Clear row */}
                        <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-3">
                                <AppIcon name="ClockIcon" size={14} className="text-text-muted opacity-60" />
                                <label className="text-[10px] text-text-muted font-bold uppercase tracking-widest opacity-60">Sort:</label>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="text-[12px] font-bold border border-border-subtle rounded-lg px-3 py-1.5 bg-app-surface text-text-primary outline-none focus:border-primary/50 appearance-none cursor-pointer shadow-subtle"
                                >
                                    <option value="latest" className="bg-app-surface text-text-primary">Latest First</option>
                                    <option value="relevance" className="bg-app-surface text-text-primary">Most Relevant</option>
                                </select>
                            </div>
                            <button
                                onClick={() => {
                                    setFilterSenderId('');
                                    setFilterChannelId('');
                                    setFilterFromDate('');
                                    setFilterToDate('');
                                    setSortBy('latest');
                                }}
                                className="text-[10px] text-primary font-bold uppercase tracking-widest hover:bg-primary/10 px-3 py-2 rounded-lg transition-all"
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>
                )}

                {/* Source Badge */}
                {hasSearched && (
                    <div className="px-6 py-2.5 flex items-center justify-between border-b border-border-subtle bg-app-bg/10">
                        <span className="text-[10px] font-bold text-text-muted uppercase tracking-widest opacity-60">
                            {isLoading ? 'Searching...' : `${total} result${total !== 1 ? 's' : ''} found`}
                        </span>
                        {source && (
                            <span
                                className={`text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-widest shadow-subtle ${source === 'elasticsearch'
                                        ? 'bg-green-500/10 text-green-600 border border-green-500/20'
                                        : 'bg-yellow-500/10 text-yellow-600 border border-yellow-500/20'
                                    }`}
                            >
                                {source === 'elasticsearch' ? '⚡ ElasticSearch' : '🔄 MongoDB Fallback'}
                            </span>
                        )}
                    </div>
                )}

                {/* Results List */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-2">
                    {/* Loading */}
                    {isLoading && results.length === 0 && (
                        <div className="flex justify-center items-center py-16">
                            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}

                    {/* Empty state — no results */}
                    {!isLoading && hasSearched && results.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 text-text-muted opacity-40">
                            <AppIcon name="MagnifyingGlassIcon" size={48} className="mb-4" />
                            <p className="text-[13px] font-bold uppercase tracking-widest">No messages found</p>
                            <p className="text-[11px] font-bold mt-1 opacity-60">Try a different keyword or adjust filters</p>
                        </div>
                    )}

                    {/* Initial empty state — nothing typed yet */}
                    {!hasSearched && !isLoading && (
                        <div className="flex flex-col items-center justify-center py-16 text-text-muted opacity-30">
                            <AppIcon name="MagnifyingGlassIcon" size={48} className="mb-4" />
                            <p className="text-[13px] font-bold uppercase tracking-widest">Type to search</p>
                            <p className="text-[11px] font-bold mt-1 opacity-60 text-center">Search across all messages, channels, and conversations</p>
                        </div>
                    )}

                    {/* Results */}
                    {results.length > 0 && (
                        <div className="space-y-2">
                            {results.map((result, index) => {
                                const isChannel = isChannelResult(result);
                                const messageText = isChannel ? result.text : result.message;
                                // const highlightField = isChannel
                                //     ? result.highlight?.text
                                //     : result.highlight?.message;

                                const highlightField = isChannel
    ? (result.highlight?.text || result.highlight?.senderName)
    : (result.highlight?.message || result.highlight?.sender || result.highlight?.receiver);

                                // sender/receiver may be a populated object or a plain string
                                const resolveName = (val) => {
                                    if (!val) return 'Unknown';
                                    if (typeof val === 'string') return val;
                                    return val.name || val.email || val.username || 'Unknown';
                                };

                                const senderName = isChannel
                                    ? result.senderName
                                    : resolveName(result.sender);
                                const locationLabel = isChannel
                                    ? `# ${result.channelName || 'Channel'}`
                                    : `DM · ${resolveName(result.sender)} → ${resolveName(result.receiver)}`;

                                const isSelected = index === selectedIndex;

                                return (
                                    <div
                                        key={result.messageId || index}
                                        ref={(el) => (resultRefs.current[index] = el)}
                                        onClick={() => handleResultClick(result)}
                                        className={`flex items-start gap-4 p-4 rounded-2xl cursor-pointer transition-all border shadow-subtle ${isSelected
                                                ? 'bg-primary/10 border-primary/30 scale-[1.01]'
                                                : 'bg-app-surface border-border-subtle hover:bg-black/5'
                                            }`}
                                        role="option"
                                        aria-selected={isSelected}
                                    >
                                        {/* Icon */}
                                        <div className="flex-shrink-0 mt-0.5">
                                            {isChannel ? (
                                                <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20 shadow-subtle">
                                                    <AppIcon name="HashtagIcon" size={22} className="text-primary" />
                                                </div>
                                            ) : (
                                                <div className="w-11 h-11 rounded-2xl bg-black/5 flex items-center justify-center border border-border-subtle shadow-subtle">
                                                    <AppIcon name="UserIcon" size={22} className="text-text-muted" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                                <span className="text-[14px] font-bold text-text-primary">
                                                    {senderName}
                                                </span>
                                                <span className="text-[10px] font-bold text-text-muted uppercase tracking-widest opacity-60">in</span>
                                                <span className="text-[10px] font-extrabold text-primary uppercase tracking-widest">
                                                    {locationLabel}
                                                </span>
                                                <span className="ml-auto text-[10px] font-bold text-text-muted uppercase tracking-widest opacity-60 flex-shrink-0">
                                                    {formatTime(result.timestamp)}
                                                </span>
                                            </div>
                                            <div className="text-[13px] text-text-secondary leading-relaxed line-clamp-2 [&_mark]:bg-primary/20 [&_mark]:text-primary [&_mark]:font-bold [&_mark]:px-1 [&_mark]:rounded">
                                                <HighlightedText
                                                    text={messageText}
                                                    highlight={highlightField}
                                                    maxLength={120}
                                                />
                                            </div>
                                        </div>

                                        {/* Arrow indicator */}
                                        <div
                                            className={`flex-shrink-0 text-primary mt-1.5 transition-all ${isSelected ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
                                                }`}
                                        >
                                            <AppIcon name="ArrowRightIcon" size={16} />
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Load More */}
                            {hasMore && (
                                <div className="flex justify-center py-4">
                                    <button
                                        onClick={loadMore}
                                        disabled={isLoading}
                                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-[11px] font-bold text-primary hover:bg-primary/5 uppercase tracking-widest transition-all disabled:opacity-50"
                                    >
                                        {isLoading ? (
                                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            `Load more (${total - results.length} remaining)`
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-app-bg/50 border-t border-border-subtle flex items-center gap-6 text-[9px] font-bold text-text-muted uppercase tracking-widest">
                    <span className="flex items-center gap-2">
                        <kbd className="px-2 py-1 bg-app-surface rounded-lg border border-border-subtle text-text-primary shadow-subtle font-black">↑↓</kbd>
                        navigate
                    </span>
                    <span className="flex items-center gap-2">
                        <kbd className="px-2 py-1 bg-app-surface rounded-lg border border-border-subtle text-text-primary shadow-subtle font-black">Enter</kbd>
                        open
                    </span>
                    <span className="flex items-center gap-2">
                        <kbd className="px-2 py-1 bg-app-surface rounded-lg border border-border-subtle text-text-primary shadow-subtle font-black">Esc</kbd>
                        close
                    </span>
                </div>
            </div>
        </div>
    );
}
